import {
  preferredHPFormNameMapping,
  preferredIntakeFormNameMapping,
  preferredProgressFormNameMapping,
  preferredSOAPFormNameMapping
} from 'constants';
import { startCase } from 'lodash';

export function replaceStrongWithDiv(htmlString) {
  // Use a regular expression to match <strong> tags and their following content
  const regex = /(<strong>.*?<\/strong>)([^<]*)/g;

  // Replace each match with a <div> containing the <strong> and its value
  const finalHTML = htmlString.replace(regex, '<div>$1$2</div>');
  return finalHTML;
}

export function extractOnlyClinicalNoteKeys(inputObject) {
  const allowedKeys = [
    'id',
    'user_id',
    'practitioner_id',
    'created_at',
    'updated_at',
    'practice_id',
    'notes',
    'present_illness',
    'state',
    'appointment_id',
    'subjective',
    'objective',
    'assessment',
    'plan',
    'cpt_codes',
    'icd_10_codes',
    'other_complaints',
    'pdf_selection',
    'locked',
    'exam',
    'test',
    'epn',
    'goals',
    'mha',
    'lock_history',
    'goals_narrative',
    'charts3d',
    'no_known_medications',
    'components_order',
    'previous_state',
    'treatment_plan_id',
    'ros',
    'hpi'
  ];

  return Object.keys(inputObject || {})
    .filter((key) => allowedKeys.includes(key))
    .reduce((obj, key) => {
      obj[key] = removeEmptyTags(inputObject[key]);
      return obj;
    }, {});
}

export function extractOnlyCurrentHPOverviewKeys(inputObject) {
  const allowedKeys = ['socialHistory', 'medicalHistory'];

  return Object.keys(inputObject || {})
    .filter((key) => allowedKeys.includes(key))
    .reduce((obj, key) => {
      obj[key] = inputObject[key];
      return obj;
    }, {});
}

export const getFormsFinalNarrative = ({ clinicalNoteForms, type }) => {
  const typeFields = {
    soap: {
      preferredNames: preferredSOAPFormNameMapping,
      fields: [
        'subjective',
        'objective',
        'assessment',
        'plan',
        'medicationHistory',
        'icd_10_codes',
        'present_illness',
        'notes'
      ]
    },
    hp: {
      preferredNames: preferredHPFormNameMapping,
      fields: [
        'exam',
        'history',
        'assessment',
        'plan',
        'medicalHistory',
        'socialHistory',
        'familyHistory',
        'surgicalHistory',
        'medicationHistory',
        'other_complaints',
        'present_illness',
        'notes',
        'hpi',
        'ros'
      ]
    },
    progress: {
      preferredNames: preferredProgressFormNameMapping,
      fields: [
        'goals_narrative',
        'session',
        'progress',
        'riskFactors',
        'activeSymptoms',
        'functionalImpairment',
        'interventions',
        'progress',
        'sessionQuotesAndContents',
        'notes'
      ]
    },
    intake: {
      preferredNames: preferredIntakeFormNameMapping,
      fields: [
        'present_illness',
        'goals_narrative',
        'timeFrames',
        'riskAndCoMorbidities',
        'notes',
        'other_complaints'
      ]
    }
  };

  // Helper function to strip HTML tags and clean text
  const stripHtml = (html) => {
    if (!html || typeof html !== 'string') return '';
    return html.replace(/<[^>]+>/g, '').trim();
  };

  let finalNarrative = '';
  const fields = typeFields[type]?.fields || [];
  const preferredNames = typeFields[type]?.preferredNames || {};

  fields.forEach((field) => {
    // Handle top-level string fields
    if (clinicalNoteForms[field] && typeof clinicalNoteForms[field] === 'string') {
      finalNarrative += `${preferredNames[field] || startCase(field === 'goals_narrative' ? 'goals' : field)}: "${stripHtml(clinicalNoteForms[field])}"\n\n`;
    }
    // Handle array fields (e.g., medicationHistory, socialHistory, familyHistory, surgicalHistory)
    else if (Array.isArray(clinicalNoteForms[field])) {
      const narratives = clinicalNoteForms[field]
        .map((item) => {
          if (item.current_medication_narrative) {
            return stripHtml(item.current_medication_narrative);
          } else if (item.response && item.response.narrative) {
            return stripHtml(item.response.narrative);
          }
          return '';
        })
        .filter((narrative) => narrative); // Remove empty narratives

      if (narratives.length > 0) {
        const fieldName = field === 'medicationHistory' ? 'Medication History' : startCase(field);
        finalNarrative += `${fieldName}: "${narratives.join('\n')}"\n\n`;
      }
    }
    // Handle nested epn fields
    else if (clinicalNoteForms.epn && clinicalNoteForms.epn[field]) {
      if (clinicalNoteForms.epn[field].narrative) {
        finalNarrative += `${startCase(field)}: "${stripHtml(clinicalNoteForms.epn[field].narrative)}"\n\n`;
      } else if (
        field === 'sessionQuotesAndContents' &&
        clinicalNoteForms.epn.sessionQuotesAndContents
      ) {
        finalNarrative += `Session Quotes And Contents: "${stripHtml(clinicalNoteForms.epn.sessionQuotesAndContents)}"\n\n`;
      }
    }
    // Handle nested mha fields
    else if (clinicalNoteForms.mha && clinicalNoteForms.mha[field]) {
      finalNarrative += `${startCase(field)}: "${stripHtml(clinicalNoteForms.mha[field])}"\n\n`;
    }
    // Handle hpi narrative
    else if (field === 'hpi' && clinicalNoteForms.hpi && clinicalNoteForms.hpi.narrative) {
      finalNarrative += `History of Present Illness: "${stripHtml(clinicalNoteForms.hpi.narrative)}"\n\n`;
    }
    // Handle ros narrative
    else if (field === 'ros' && clinicalNoteForms.ros && clinicalNoteForms.ros.narrative) {
      finalNarrative += `Review of Systems: "${stripHtml(clinicalNoteForms.ros.narrative)}"\n\n`;
    }
    // Handle medicalHistory narrative
    else if (
      field === 'medicalHistory' &&
      clinicalNoteForms.medicalHistory &&
      clinicalNoteForms.medicalHistory.response &&
      clinicalNoteForms.medicalHistory.response.narrative
    ) {
      finalNarrative += `Medical History: "${stripHtml(clinicalNoteForms.medicalHistory.response.narrative)}"\n\n`;
    }
  });

  // Remove extra newlines, fix single quotes, and trim the final string
  return finalNarrative
    .replace(/\n{3,}/g, '\n\n')
    .replace(/&apos;/g, "'")
    .trim();
};
export function removeEmptyTags(htmlContent) {
  // Create a temporary DOM element to parse the HTML
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, 'text/html');

  // Find all li and p elements
  const elements = doc.querySelectorAll('li, p');

  // Remove elements that are empty or contain only <br> or whitespace
  elements.forEach((element) => {
    // Check if element is empty or contains only whitespace or <br>
    const isEmpty =
      element.innerHTML.trim() === '' ||
      element.innerHTML.trim() === '<br>' ||
      element.innerHTML.trim() === ' ';

    if (isEmpty) {
      element.remove();
    }
  });

  // Return the cleaned HTML content
  return doc.body.innerHTML;
}
